import type {
  MunicipalityApiPayload,
  MunicipalityDocument,
  MunicipalityNote,
  MunicipalityRecord,
  SelectOption,
} from '../interfaces/municipalityRegistration.interface';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const unwrapApiData = (response: any): any => {
  if (response == null) return response;
  if (
    typeof response === 'object' &&
    Object.prototype.hasOwnProperty.call(response, 'status') &&
    Object.prototype.hasOwnProperty.call(response, 'data')
  ) {
    return response.data;
  }
  return response;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const firstOf = (value: any): any =>
  Array.isArray(value) && value.length ? value[0] : value;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const normalizeType = (type: any, typeIdFromRow: number | null | undefined) => {
  if (Array.isArray(type)) {
    const labels: string[] = type
      .map((t: unknown) =>
        typeof t === 'string' ? t : (t as SelectOption)?.label
      )
      .filter(Boolean);
    const joined = labels.join(', ');
    const head = firstOf(type);
    let normalized: object | null = null;
    if (head && typeof head === 'object') {
      normalized = { ...head, label: joined || (head as SelectOption).label };
    } else if (joined) {
      normalized = { label: joined };
    }
    const tid =
      typeIdFromRow ??
      (head && typeof head === 'object'
        ? ((head as Record<string, unknown>).type_id ??
          (head as SelectOption).value)
        : undefined);
    return {
      type: normalized,
      typeId: tid,
      typeLabel: joined || (normalized as SelectOption)?.label || '',
    };
  }
  if (type && typeof type === 'object') {
    const lbl = (type as SelectOption).label ?? '';
    return {
      type,
      typeId:
        typeIdFromRow ??
        (type as Record<string, unknown>).type_id ??
        (type as SelectOption).value,
      typeLabel: lbl,
    };
  }
  if (typeof type === 'string' && type) {
    return { type: { label: type }, typeId: typeIdFromRow, typeLabel: type };
  }
  return { type: null, typeId: typeIdFromRow, typeLabel: '' };
};

const normalizeStatus = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  status: any,
  statusIdFromRow: number | null | undefined
) => {
  if (Array.isArray(status)) {
    const labels: string[] = status
      .map((s: unknown) =>
        typeof s === 'string'
          ? s
          : ((s as Record<string, string>)?.label ??
            (s as Record<string, string>)?.status ??
            ((s as SelectOption)?.value as string))
      )
      .filter(Boolean);
    const joined = labels.join(', ');
    const head = firstOf(status);
    let normalized: object | null = null;
    if (head && typeof head === 'object') {
      normalized = {
        ...head,
        status: joined || (head as Record<string, string>).status,
        label: joined || (head as SelectOption).label,
      };
    } else if (joined) {
      normalized = { status: joined, label: joined };
    }
    const sid =
      statusIdFromRow ??
      (head && typeof head === 'object'
        ? ((head as Record<string, unknown>).status_id ??
          (head as SelectOption).value)
        : undefined);
    return {
      status: normalized,
      statusId: sid,
      statusLabel:
        joined ||
        (normalized as Record<string, string>)?.status ||
        (normalized as SelectOption)?.label ||
        '',
    };
  }
  if (status && typeof status === 'object') {
    const lbl =
      (status as Record<string, string>).label ??
      (status as Record<string, string>).status ??
      '';
    return {
      status,
      statusId:
        statusIdFromRow ??
        (status as Record<string, unknown>).status_id ??
        (status as SelectOption).value,
      statusLabel: lbl,
    };
  }
  if (typeof status === 'string' && status) {
    return {
      status: { status, label: status },
      statusId: statusIdFromRow,
      statusLabel: status,
    };
  }
  return { status: null, statusId: statusIdFromRow, statusLabel: '' };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const normalizeState = (state: any) => {
  if (Array.isArray(state)) {
    const parts: string[] = state
      .map((s: unknown) =>
        typeof s === 'string'
          ? s
          : ((s as Record<string, string>)?.label ??
            (s as Record<string, string>)?.state_name ??
            (s as Record<string, string>)?.state_code)
      )
      .filter(Boolean);
    const joined = parts.join(', ');
    return { state: firstOf(state), stateLabel: joined || '-' };
  }
  if (state && typeof state === 'object') {
    const s = state as Record<string, string>;
    const joined = [s.state_name, s.state_code].filter(Boolean).join(' ');
    const lbl = s.label ?? (joined || s.state_code || s.state_name || '');
    return { state, stateLabel: lbl };
  }
  if (typeof state === 'string') {
    return { state: { label: state }, stateLabel: state };
  }
  return { state: null, stateLabel: '' };
};

/** Normalize DB/API yes-no values (0/1, booleans, Yes/No strings) for list display. */
export const triStateDbToList = (v: unknown): boolean | null => {
  if (v === 1 || v === true) return true;
  if (v === 0 || v === false) return false;
  if (typeof v === 'string') {
    const normalized = v.trim().toLowerCase();
    if (['yes', 'y', 'true', '1'].includes(normalized)) return true;
    if (['no', 'n', 'false', '0'].includes(normalized)) return false;
  }
  return null;
};

const yesNoToDbValue = (v: unknown): 0 | 1 | null => {
  const normalized = triStateDbToList(v);
  if (normalized === true) return 1;
  if (normalized === false) return 0;
  return null;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const mapDocumentForUi = (doc: any): MunicipalityDocument => ({
  id: doc.id ?? doc.municipality_registration_document_id,
  fileName: doc.fileName ?? doc.file_name,
  documentUrl: doc.documentUrl ?? doc.file_url,
  uploadedBy: doc.uploadedBy ?? doc.created_by,
  uploadDate: doc.uploadDate ?? doc.created_at,
  documentType: doc.documentType ?? doc.document_type,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const resolveUserDisplayName = (user: any): string => {
  if (!user) return '';
  if (typeof user === 'string') return user.trim();
  const first = user.first_name || user.firstName || '';
  const last = user.last_name || user.lastName || '';
  const name = `${first} ${last}`.trim();
  if (name) return name;
  if (user.fullname) return String(user.fullname).trim();
  if (user.full_name) return String(user.full_name).trim();
  if (user.user_id) return String(user.user_id);
  return '';
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const mapNoteForUi = (note: any): MunicipalityNote => ({
  id: note.id ?? note.note_id,
  text: note.text ?? note.note_text,
  createdBy:
    resolveUserDisplayName(note.createdBy) ||
    resolveUserDisplayName(note.created_by) ||
    '',
  dateCreated: note.dateCreated ?? note.created_at,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
/** Stored category values: project_category_id numbers and legacy name strings. */
export const parseCategoryFromApi = (
  category: unknown
): (number | string)[] => {
  if (category == null) return [];
  const raw = Array.isArray(category) ? category : [category];
  const parsed: (number | string)[] = [];

  raw.forEach(item => {
    const numeric = Number(item);
    if (Number.isInteger(numeric) && numeric > 0) {
      parsed.push(numeric);
      return;
    }
    if (typeof item === 'object' && item != null) {
      const fromObj = Number(
        (item as { project_category_id?: number }).project_category_id
      );
      if (Number.isInteger(fromObj) && fromObj > 0) {
        parsed.push(fromObj);
        return;
      }
    }
    const label = String(item ?? '').trim();
    if (label) parsed.push(label);
  });

  const seen = new Set<string>();
  return parsed.filter(item => {
    const key =
      typeof item === 'number' ? `n:${item}` : `s:${item.toLowerCase()}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const parseCategoryIdsFromApi = (category: unknown): number[] =>
  parseCategoryFromApi(category).filter(
    (item): item is number => typeof item === 'number'
  );

export const toProjectCategoryIdsPayload = (
  category: unknown
): number[] | null => {
  const ids = parseCategoryIdsFromApi(category);
  return ids.length ? ids : null;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const apiToUi = (record: any): MunicipalityRecord => {
  const r = record?.dataValues ?? record;
  const nt = normalizeType(r.type, r.type_id);
  const ns = normalizeStatus(r.status, r.status_id);
  const nState = normalizeState(r.state);
  const isPlumbingType = nt.typeLabel === 'Plumbing';

  return {
    id: r.municipality_registration_id ?? r.id,
    stateId:
      r.state_id ??
      (nState.state as Record<string, unknown>)?.state_id ??
      (nState.state as SelectOption)?.value,
    state: nState.state,
    stateLabel: nState.stateLabel,
    city: r.city,
    county: r.county,
    municipalityName: r.municipality_name,
    district: Array.isArray(r.district)
      ? r.district
      : r.district
        ? [r.district]
        : [],
    zipCode: r.zip_code,
    typeId: nt.typeId ?? r.type_id,
    type: nt.type,
    typeLabel: nt.typeLabel,
    issueDate: r.issue_date,
    expiryDate: r.expiry_date,
    category: parseCategoryFromApi(r.category),
    categoryLabels: Array.isArray(r.category_labels) ? r.category_labels : [],
    statusId: ns.statusId ?? r.status_id,
    status: ns.status,
    statusLabel: ns.statusLabel,
    documentNumber: r.document_number,
    permitRequired: r.permit_required ?? r.permitRequired,
    registrationRequired: r.registration_required ?? r.registrationRequired,
    subContractorRegistrationRequired:
      r.subcontractor_registration_required ??
      r.subContractorRegistrationRequired,
    feeType: r.fee_type,
    cost: r.cost,
    costFormula: r.cost_formula,
    submissionType: r.submission_type,
    dishwasher: isPlumbingType ? r.window : '',
    waterHeater: isPlumbingType ? r.patio_door : '',
    window: isPlumbingType ? '' : r.window,
    patioDoor: isPlumbingType ? '' : r.patio_door,
    entryDoor: r.entry_door,
    stormDoor: r.storm_door,
    doorGarageHome: r.door_garage_home,
    interior: r.interior,
    inspectionRequired: r.inspection_required,
    inspectionNotes: isPlumbingType ? r.notes_for_review_team : '',
    permitIssuanceLeadTime: r.permit_issuance_lead_time,
    notesForReviewTeam: isPlumbingType ? '' : r.notes_for_review_team,
    mainContact: r.main_contact,
    phone: r.phone_number,
    ext: r.phone_extension,
    email: r.email,
    website: r.website,
    additionalInfo: r.additional_information,
    documents: (r.documents || []).map(mapDocumentForUi),
    notes: (r.notes || []).map(mapNoteForUi),
  };
};

const toNullableId = (v: unknown): number | null => {
  if (v === '' || v === undefined || v === null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export const buildMunicipalityApiPayload = (
  values: Partial<MunicipalityRecord>
): MunicipalityApiPayload => {
  const isPlumbingType =
    (values as Record<string, unknown>).typeLabel === 'Plumbing';

  return {
    state_id: toNullableId(values.stateId),
    city: values.city || null,
    county: values.county || null,
    municipality_name: values.municipalityName ?? '',
    district: (values.district?.length ? values.district : null) as
      | string[]
      | null,
    zip_code: values.zipCode || null,
    type_id: toNullableId(values.typeId),
    issue_date: (values.issueDate as string) || null,
    expiry_date: (values.expiryDate as string) || null,
    category: toProjectCategoryIdsPayload(values.category),
    status_id: toNullableId(values.statusId),
    document_number: values.documentNumber || null,
    permit_required: yesNoToDbValue(values.permitRequired),
    registration_required: yesNoToDbValue(values.registrationRequired),
    subcontractor_registration_required: yesNoToDbValue(
      values.subContractorRegistrationRequired
    ),
    fee_type: values.feeType || null,
    cost: values.cost || null,
    cost_formula: values.costFormula || null,
    submission_type: values.submissionType || null,
    window: isPlumbingType
      ? (values as Record<string, string>).dishwasher || null
      : values.window || null,
    patio_door: isPlumbingType
      ? (values as Record<string, string>).waterHeater || null
      : values.patioDoor || null,
    entry_door: isPlumbingType ? null : values.entryDoor || null,
    storm_door: isPlumbingType ? null : values.stormDoor || null,
    door_garage_home: isPlumbingType ? null : values.doorGarageHome || null,
    interior: isPlumbingType ? null : values.interior || null,
    inspection_required: values.inspectionRequired || null,
    permit_issuance_lead_time: values.permitIssuanceLeadTime || null,
    notes_for_review_team: isPlumbingType
      ? (values as Record<string, string>).inspectionNotes || null
      : values.notesForReviewTeam || null,
    main_contact: values.mainContact || null,
    phone_number: values.phone || null,
    phone_extension: values.ext || null,
    email: values.email || null,
    website: values.website || null,
    additional_information: values.additionalInfo || null,
  };
};

const normalizeDateForCompare = (value: unknown): string | null => {
  if (value == null || value === '') return null;
  const date = value instanceof Date ? value : new Date(value as string);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10);
};

const normalizeComparablePayload = (
  payload: MunicipalityApiPayload
): string => {
  const normalized = {
    ...payload,
    issue_date: normalizeDateForCompare(payload.issue_date),
    expiry_date: normalizeDateForCompare(payload.expiry_date),
    district: [...(payload.district || [])].map(String).sort(),
    category: [...(payload.category || [])]
      .map(Number)
      .filter(Number.isFinite)
      .sort(),
    cost:
      payload.cost === null || payload.cost == null
        ? null
        : Number(payload.cost),
  };
  return JSON.stringify(normalized);
};

export const buildComparableMunicipalityPayload = (
  formValues: Partial<MunicipalityRecord>
): string =>
  normalizeComparablePayload(buildMunicipalityApiPayload(formValues));

export const areMunicipalityFormValuesEqual = (
  left: Partial<MunicipalityRecord>,
  right: Partial<MunicipalityRecord>
): boolean =>
  buildComparableMunicipalityPayload(left) ===
  buildComparableMunicipalityPayload(right);
